import React from 'react';
import {
  FaMicroscope,
  FaBookOpen,
  FaChartLine,
  FaSeedling,
  FaFlask,
  FaDatabase,
  FaPuzzlePiece,
  FaLeaf,
  FaGlobe,
  FaImages // FaImages icon used in the new section
} from 'react-icons/fa';

const BANNER_IMAGE = "https://www.shutterstock.com/image-photo/child-hands-holding-fertile-soil-260nw-1862529433.jpg";
const PRIMARY_COLOR = '#4f6a39';
const ACCENT_COLOR = '#84cc16';

// --- NEW IMAGE DATA ---
const fieldResultsImages = [
    { src: "https://bharatvarshnaturefarms.com/wp-content/uploads/2022/08/Plant-Growth-Vermicompost.jpg", alt: "Healthy Wheat Field Result" },
    { src: "https://mir-s3-cdn-cf.behance.net/projects/404/86f347174114755.Y3JvcCwxMDgwLDg0NCwwLDExNw.jpeg", alt: "Vibrant Vegetable Harvest" },
    { src: "https://i.fbcd.co/products/resized/resized-750-500/c-1000-designbundle-hand-farmer-inspecting-soil-health-before-planting07-09-08-248085dd918c72ef93b43fb0029fcfe52b195a8441abb7867b24882570e65a7f.jpg", alt: "Farmer Inspecting Soil" },
    { src: "https://thumbs.dreamstime.com/b/raised-vegetable-garden-beds-lush-green-lettuce-marigold-flowers-tropical-organic-backyard-farm-setting-340720617.jpg", alt: "Lush Green Organic Farm" },
    { src: "https://www.shutterstock.com/image-photo/corn-field-india-600nw-40893322.jpg", alt: "High Yield Corn Crop" },
    { src: "https://thumbs.dreamstime.com/b/fertile-soil-giving-life-to-new-plants-growing-next-to-dry-barren-land-young-plants-growing-fertile-soil-next-to-dry-351528292.jpg", alt: "Close-up of Fertile Soil" },
];
// ----------------------

const focusAreas = [
  {
    icon: FaMicroscope,
    title: "Advanced Vermiculture Technology",
    description:
      "In-depth research to accelerate organic waste breakdown and maximize nutrient density in vermicompost through advanced microbial inoculation techniques."
  },
  {
    icon: FaSeedling,
    title: "Precision Organic Soil Mapping",
    description:
      "Utilizing remote sensing and proprietary protocols to map nutrient deficiencies and biological activity for precision application of organic inputs."
  },
  {
    icon: FaChartLine,
    title: "Crop Performance & Bio-fortification",
    description:
      "Rigorous field trials to quantify the impact of organic inputs on crop yield, nutritional content, and resistance to pests and diseases."
  },
  {
    icon: FaFlask,
    title: "Zero-Waste System Integrity",
    description:
      "Dedicated R&D on closing the loop in integrated farming, optimizing waste streams (manure/residue) into valuable, sustainable organic inputs."
  }
];

// --- Responsive Component: FocusCard (No Change) ---
const FocusCard: React.FC<{ area: typeof focusAreas[0] }> = ({ area }) => (
  <div
    className="bg-white p-6 rounded-xl shadow-lg border-t-4 hover:shadow-2xl hover:scale-[1.03] transform transition duration-300 h-full flex flex-col items-start justify-start"
    style={{ borderColor: ACCENT_COLOR }}
  >
    <area.icon className="text-4xl mb-4" style={{ color: PRIMARY_COLOR }} />
    <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{area.title}</h4>
    <p className="text-gray-600 text-sm leading-relaxed">{area.description}</p>
  </div>
);


const ScienceStudiesPageEnhanced: React.FC = () => {
  return (
    <div className="bg-gray-50 mt-32">
      
      {/* 1. Hero Section */}
      <div
        className="relative bg-cover bg-center h-64 sm:h-80 md:h-[350px] flex flex-col justify-center px-4 sm:px-10"
        style={{ backgroundImage: `url(${BANNER_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-green-900/50"></div>
        <div className="relative z-10 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold text-white uppercase tracking-tight mb-3 sm:mb-4">
            Science & Studies
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-2xl">
            Bridging <span className="text-[#84cc16] font-semibold">Nature</span> and{" "}
            <span className="text-orange-400 font-semibold">Innovation</span> to
            build a scientifically validated organic revolution.
          </p>
        </div>
      </div>

      {/* 2. Introduction */}
      <div className="py-12 sm:py-20 px-4 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-8 sm:mb-10 text-center">
            Our Scientific Commitment to an Organic World
          </h2>
          <div className="md:flex md:space-x-12 items-start">
            
            <div className="md:w-3/5 text-gray-700">
              <p className="text-base sm:text-lg mb-5 leading-relaxed">
                Our mission for a <strong>Healthy Nation & Organic World</strong> is powered by data-driven,
                evidence-based research. We believe sustainability should not just be an idea—it must be measurable,
                reproducible, and grounded in <span className="text-green-700 font-semibold">scientific integrity</span>.
              </p>
              <p className="text-base sm:text-lg mb-5 leading-relaxed">
                Every discovery contributes toward perfecting our zero-waste, regenerative farming models. From soil microbiology
                to bio-fortification research, we combine tradition with innovation to make agriculture truly sustainable.
              </p>
              <p className="italic text-sm border-l-4 pl-4" style={{ borderColor: PRIMARY_COLOR }}>
                “Science gives our soil a voice — helping us understand the living systems beneath our feet that feed
                the world above.”
              </p>
            </div>

            <div
              className="md:w-2/5 flex-shrink-0 p-6 sm:p-8 rounded-xl mt-8 md:mt-0 shadow-xl bg-gradient-to-b from-[#e8fce8] to-[#f7fff0] border-l-4"
              style={{ borderColor: ACCENT_COLOR }}
            >
              <h3 className="text-xl sm:text-2xl font-bold mb-4 text-[#4f6a39]">Key Research Pillars</h3>
              <div className="space-y-4 text-gray-700 font-medium text-sm sm:text-base">
                <div className="flex items-center"><FaDatabase className="mr-3 text-xl text-[#4f6a39]" /> Data-Driven Organic Systems</div>
                <div className="flex items-center"><FaPuzzlePiece className="mr-3 text-xl text-[#4f6a39]" /> Integrated Farm Ecosystems</div>
                <div className="flex items-center"><FaBookOpen className="mr-3 text-xl text-[#4f6a39]" /> Peer-Reviewed Collaborations</div>
                <div className="flex items-center"><FaGlobe className="mr-3 text-xl text-[#4f6a39]" /> Global Sustainability Partnerships</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Focus Areas */}
      <div className="py-12 sm:py-20 px-4 sm:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Four Pillars of Scientific Inquiry
          </h2>
          <p className="text-gray-600 mb-10 sm:mb-12 max-w-3xl mx-auto">
            Our R&D explores every layer of nature’s intelligence—from microscopic analysis to on-field innovation—
            ensuring that each organic product is backed by science and built for the planet.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {focusAreas.map((area, index) => (
              <FocusCard key={index} area={area} />
            ))}
          </div>
        </div>
      </div>

      {/* 4. NEW SECTION: Visual Field Results (Image Gallery) */}
      <div className="py-12 sm:py-20 px-4 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* New Heading */}
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Visual Proof: Our Impact on the Field
          </h2>
          {/* New Description */}
          <p className="text-center text-gray-600 mb-10 sm:mb-12 max-w-4xl mx-auto">
            Witness the success of our organic approach through visual evidence from real-world trials and farmer collaborations.
          </p>

          {/* Responsive Image Grid: 2 columns on mobile, 3 on tablet/desktop */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {fieldResultsImages.map((image, index) => (
              <div key={index} className="rounded-xl overflow-hidden shadow-lg group relative cursor-pointer">
                {/* Image */}
                <img
                  src={image.src}
                  alt={image.alt}
                  // Ensured consistent aspect ratio and object-cover for responsiveness
                  className="w-full h-40 sm:h-56 object-cover transition-opacity duration-300 group-hover:opacity-80"
                />
                
                {/* Overlay Caption */}
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-white text-sm sm:text-base font-semibold">
                    <FaImages className='inline-block mr-2 text-sm' style={{ color: ACCENT_COLOR }} />
                    {image.alt}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScienceStudiesPageEnhanced;